import { CheckCircle } from "@mui/icons-material";
import { Box, Button, Typography } from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { grey, indigo } from "../../../../colors";
import ClarifyingContainer from "./ClarifyingContainer";
import QuestionOptions from "./QuestionOptions";
import {
  CLARIFY_STORAGE_KEY,
  ClarifyingQuestionsProps,
  OTHER_OPTION,
  PersistedClarifyState,
  QuestionSelection,
  clearPersistedState,
  initialSelection,
  readPersistedState,
} from "./types";

// A direct clarifying question: the user answers with a predefined option, a facet dropdown, or
// free text. Submitting appends the answer as context and re-sends the query.
export default function ClarifyingQuestionsForm({
  clarification,
  answered,
  sessionId,
  prefillValues,
  onSubmit,
  onSkip,
  onSkipAndTurnOff,
}: ClarifyingQuestionsProps) {
  // Backend ranks questions and returns the best one to clarify
  const question = clarification.questions?.[0];
  const questionIds = useMemo(() => (question ? [question.id] : []), [question]);

  // Restore a draft only while the clarification is still active; answered items must not resurrect it.
  const persisted = useMemo(() => (!answered ? readPersistedState(sessionId, questionIds) : null), []);

  const [selection, setSelection] = useState<QuestionSelection>(() => {
    const saved = question ? persisted?.selections?.[question.id] : undefined;
    if (saved) return { selected: new Set(saved.selected ?? []), otherText: saved.otherText ?? "" };
    if (prefillValues && prefillValues.length > 0 && question) {
      const options = new Set(question.options ?? []);
      const selected = new Set<string>();
      let otherText = "";
      for (const value of prefillValues) {
        if (options.has(value)) {
          selected.add(value);
        } else {
          otherText = value;
          selected.add(OTHER_OPTION);
        }
      }
      return { selected, otherText };
    }
    return initialSelection();
  });

  // Persist the in-progress draft so a mid-flow refresh can resume where the user left off.
  useEffect(() => {
    if (answered || !question) return;
    if (typeof window === "undefined") return;
    try {
      const payload: PersistedClarifyState = {
        sessionId,
        questionIds,
        selections: {
          [question.id]: { selected: Array.from(selection.selected), otherText: selection.otherText },
        },
        skippedIds: [],
      };
      window.localStorage.setItem(CLARIFY_STORAGE_KEY, JSON.stringify(payload));
    } catch {
      // Skip
    }
  }, [answered, question, questionIds, selection, sessionId]);

  useEffect(() => {
    if (answered) clearPersistedState();
  }, [answered]);

  // `forceMulti` lets the searchable dropdown always behave as multi-select (toggle values
  // in/out) regardless of the question's multi_select flag, since a dropdown is inherently a
  // multi-select affordance. Row-based options still honour question.multi_select.
  const toggleOption = (option: string, forceMulti = false) => {
    if (!question) return;
    setSelection((current) => {
      const nextSelected = new Set(current.selected);

      if (question.multi_select || forceMulti) {
        if (nextSelected.has(option)) {
          nextSelected.delete(option);
        } else {
          nextSelected.add(option);
        }
      } else {
        nextSelected.clear();
        nextSelected.add(option);
      }

      return { ...current, selected: nextSelected };
    });
  };

  // Typing in the "Other" field auto-selects it; clearing the text deselects it.
  const setOtherText = (text: string) => {
    if (!question) return;
    setSelection((current) => {
      const nextSelected = new Set(current.selected);
      if (text.trim()) {
        if (!question.multi_select) nextSelected.clear();
        nextSelected.add(OTHER_OPTION);
      } else {
        nextSelected.delete(OTHER_OPTION);
      }
      return { selected: nextSelected, otherText: text };
    });
  };

  const resolveSelection = (): { values: string[]; freeText: string } => {
    const values: string[] = [];
    let freeText = "";
    selection.selected.forEach((value) => {
      if (value === OTHER_OPTION) {
        freeText = selection.otherText.trim();
      } else {
        values.push(value);
      }
    });
    return { values, freeText };
  };

  const hasAnswer = selection.selected.size > 0;

  const handleSubmit = () => {
    if (!question) return;
    const { values, freeText } = resolveSelection();
    if (values.length === 0 && !freeText) return;
    onSubmit([{ field_hint: question.field_hint, question: question.question, values, freeText }]);
  };

  if (!question) {
    return (
      <ClarifyingContainer reasoning={clarification.reasoning} answered={answered} onSkipAndTurnOff={onSkipAndTurnOff}>
        {clarification.skip_available && !answered && (
          <Box display="flex" justifyContent="flex-end" mt="1em">
            <Button variant="text" onClick={onSkip} sx={{ color: grey[700] }}>
              Answer anyway
            </Button>
          </Box>
        )}
      </ClarifyingContainer>
    );
  }

  return (
    <ClarifyingContainer reasoning={clarification.reasoning} answered={answered} onSkipAndTurnOff={onSkipAndTurnOff}>
      <Typography variant="h6" color={indigo[900]} mt="0.15em" mb="0.25em">
        {question.question}
      </Typography>

      {!answered && (
        <Typography variant="body2" color={grey[600]} mb="0.5em">
          Pick an option or type your own answer in the “Other” field below.
        </Typography>
      )}

      <QuestionOptions
        question={question}
        selection={selection}
        disabled={answered}
        onToggle={(option) => toggleOption(option)}
        onToggleMulti={(option) => toggleOption(option, true)}
        onOtherText={(text) => setOtherText(text)}
      />

      {/* Footer actions */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mt="1em" flexWrap="wrap" gap="0.5em">
        {!answered && clarification.skip_available ? (
          <Button
            variant="outlined"
            onClick={onSkip}
            sx={{ color: grey[600], borderColor: grey[300], borderRadius: "20px" }}
          >
            Skip
          </Button>
        ) : (
          <Box />
        )}
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={answered || !hasAnswer}
          disableElevation
          sx={{
            borderRadius: "20px",
            px: "1.5em",
            backgroundColor: indigo[500],
            color: "#fff",
            "&:hover": { backgroundColor: indigo[700] },
            "&.Mui-disabled": { backgroundColor: grey[200], color: grey[400] },
          }}
        >
          Submit answer
        </Button>
      </Box>

      {answered && (
        <Box display="flex" alignItems="center" gap="0.4em" mt="1em" color={grey[600]}>
          <CheckCircle sx={{ fontSize: 16, color: indigo[500] }} />
          <Typography variant="caption">Thanks — refining your answer with this context.</Typography>
        </Box>
      )}
    </ClarifyingContainer>
  );
}
